import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MutationUpdateModerationSettingsArgs,
  ModerationSettings,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getModerationSettingsKey } from '../../utils/keys/moderation.key.js'

export const useUpdateModerationSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    ModerationSettings,
    ClientError,
    MutationUpdateModerationSettingsArgs,
    { snapshot: ModerationSettings }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const moderationSettingsKey = getModerationSettingsKey()

  return useMutation<
    ModerationSettings,
    ClientError,
    MutationUpdateModerationSettingsArgs,
    { snapshot: ModerationSettings }
  >(variables => client.moderation.updateSettings(variables), {
    onMutate: async newModerationSettings => {
      await queryClient.cancelQueries(moderationSettingsKey)

      const snapshot = queryClient.getQueryData<ModerationSettings>(
        moderationSettingsKey,
      )

      queryClient.setQueriesData<ModerationSettings>(
        moderationSettingsKey,
        old => {
          return {
            ...old,
            ...newModerationSettings,
          }
        },
      )

      return { snapshot }
    },
    onError: (err, newNetworkSettings, context) => {
      // revert back the optimistic response
      queryClient.setQueriesData<ModerationSettings>(
        moderationSettingsKey,
        context.snapshot,
      )
    },
    onSettled: () => {
      // invalidate to refetch again
      queryClient.invalidateQueries(moderationSettingsKey)
    },
    ...useMutationOptions,
  })
}
